/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { useAiContext } from '../providers/ai-provider';
import { ArrowUpRight, ArrowDownRight, Layers } from 'lucide-react';
import { formatCurrency } from '../lib/utils/format';
import { AiSignal } from '../types/ai';

const SignalCard: React.FC<{ signal: AiSignal }> = ({ signal }) => {
    const isBuy = signal.type === 'BUY';
    const isSui = signal.token.properties?.chain === 'SUI';
    const chainIcon = isSui ? "https://cryptologos.cc/logos/sui-sui-logo.png" : "https://cryptologos.cc/logos/solana-sol-logo.png";

    const handleAnalyze = () => {
        window.dispatchEvent(new CustomEvent('open-ai-signal', { detail: signal }));
    };

    // Determine border color based on confidence and type
    // High confidence (>80) gets Green/Red, Medium gets Amber
    let borderColor = 'border-amber-400';
    if (signal.confidence >= 80) {
        borderColor = isBuy ? 'border-green-500' : 'border-red-500';
    }

    return (
        <div className={`bg-white p-5 rounded-2xl border ${borderColor} shadow-sm flex flex-col gap-4 group transition-all hover:shadow-lg hover:-translate-y-1 relative overflow-hidden h-full`}>
            
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="relative">
                        <div className="w-10 h-10 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center shrink-0 overflow-hidden">
                            {signal.token.image ? <img src={signal.token.image} className="w-full h-full object-cover" /> : <span className="font-bold text-stone-400">{signal.token.symbol[0]}</span>}
                        </div>
                        {/* Chain Icon Overlay */}
                        <div className="absolute -bottom-1.5 -right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center border border-stone-100 shadow-sm z-10">
                            <img src={chainIcon} className="w-3 h-3 object-contain" alt={isSui ? 'Sui' : 'Solana'} />
                        </div>
                    </div>
                    <div>
                        <h4 className="font-bold text-stone-900 leading-tight truncate max-w-[100px]">{signal.token.name}</h4>
                        <span className="text-xs font-mono text-stone-500">${signal.token.symbol}</span>
                    </div>
                </div>
                <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md border ${isBuy ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                    {isBuy ? <ArrowUpRight size={14}/> : <ArrowDownRight size={14}/>} {signal.type}
                </div>
            </div>
            
            <div className="flex-1">
                <p className="text-xs text-stone-600 italic line-clamp-3">"{signal.reasoning}"</p>
            </div>

            <div className="border-t border-stone-100 pt-3 flex flex-col gap-3 mt-auto">
                <div className="flex justify-between items-end text-[10px] font-mono text-stone-500">
                    <div className="flex flex-col gap-1">
                        <span>Price: <span className="font-bold text-stone-800">{formatCurrency(signal.priceAtSignal, 'USD', 6)}</span></span>
                        <div className="flex items-center gap-1 opacity-80">
                            <span>Network:</span>
                            <img src={chainIcon} className="w-3 h-3 object-contain" alt="" />
                            <span>{isSui ? 'Sui' : 'Solana'}</span>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <span>Score: <span className={`font-bold ${signal.confidence > 80 ? 'text-green-600' : 'text-stone-800'}`}>{signal.confidence}%</span></span>
                    </div>
                </div>
                <button 
                    onClick={handleAnalyze}
                    className="w-full py-2.5 bg-stone-900 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-2 hover:bg-nobel-gold hover:text-stone-900 transition-colors"
                >
                    <Layers size={14} /> Analyze Signal
                </button>
            </div>
        </div>
    );
};

export const AiSignalsFeed: React.FC<{ limit?: number, chainFilter?: string }> = ({ limit, chainFilter }) => {
    const { signals, isLoading } = useAiContext();

    const filteredSignals = signals.filter(s => {
        if (!chainFilter || chainFilter === 'ALL') return true;
        return s.token.properties?.chain === chainFilter;
    });

    const displaySignals = limit ? filteredSignals.slice(0, limit) : filteredSignals;

    if (isLoading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(limit || 4)].map((_, i) => (
                    <div key={i} className="h-[260px] bg-white p-5 rounded-2xl border border-stone-200 animate-pulse">
                        <div className="h-4 bg-stone-100 rounded w-3/4 mb-4"></div>
                        <div className="h-16 bg-stone-100 rounded-lg mb-4"></div>
                        <div className="h-10 bg-stone-900 rounded-lg"></div>
                    </div>
                ))}
            </div>
        );
    }

    if (displaySignals.length === 0) {
        return <div className="text-center py-16 text-stone-400 bg-white border border-stone-200 rounded-2xl">No signals found.</div>;
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displaySignals.map(signal => (
                <SignalCard key={signal.id} signal={signal} />
            ))}
        </div>
    );
};